import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, Button, Grid, Chip, LinearProgress, Divider,
  AppBar, Toolbar, IconButton, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert, CircularProgress, Switch, FormControlLabel
} from '@mui/material';
import {
  ArrowBack, Hub, Add, Refresh, Router, NetworkCheck, Speed,
  CheckCircle, Warning, Error, PlayArrow, Stop, Edit, Delete, Settings, TrendingUp
} from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import strictMonochromeTheme from '../utils/strictMonochromeTheme';

const AVO_NETWORKS = {
  devnet: { rpcUrl: 'http://localhost:9545', chainId: 1337 },
  testnet: { rpcUrl: 'http://localhost:9546', chainId: 1338 },
  mainnet: { rpcUrl: 'http://localhost:9547', chainId: 1339 }
};

const BootstrapNodes = () => {
  const navigate = useNavigate();
  const [nodes, setNodes] = useState([]);
  const [networkMetrics, setNetworkMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createDialog, setCreateDialog] = useState(false);
  const [newNode, setNewNode] = useState({
    amount: '100000' // Default 100k AVO for bootstrap stake
  });

  const callRpcMethod = async (method, params = []) => {
    const network = localStorage.getItem('network') || 'devnet';
    const networkConfig = AVO_NETWORKS[network];
    const response = await fetch(networkConfig.rpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jsonrpc: '2.0', id: 1, method: method, params: params })
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (data.error) throw new Error(data.error.message || 'RPC error');
    return data.result;
  };

  // Función para obtener nodos bootstrap REALES del RPC
  const fetchBootstrapNodes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      console.log('🔍 Fetching bootstrap nodes from AVO RPC...');
      const result = await callRpcMethod('avo_getBootstrapNodes', []);
      
      console.log('📋 Bootstrap nodes received:', result);
      
      const bootstrapNodes = (result.bootstrap_nodes || []).map(node => ({
        id: node.id,
        name: `Bootstrap-${node.address.slice(0, 8)}`,
        address: node.address,
        stakeAmount: node.stake_amount_avo,
        stakeAmountWei: node.stake_amount_wei,
        accumulatedRewards: node.accumulated_rewards_avo,
        pendingRewards: node.pending_rewards_avo,
        totalRewards: node.total_rewards_avo,
        apr: node.apr,
        createdAt: node.created_at,
        lastRewardUpdate: node.last_reward_update,
        status: node.status.toLowerCase(),
        stakeType: node.stake_type
      }));
      
      setNodes(bootstrapNodes);
      setNetworkMetrics(result.network_metrics || null);
      
      console.log(`✅ Loaded ${bootstrapNodes.length} bootstrap nodes`);
    } catch (error) {
      console.error('❌ Error fetching bootstrap nodes:', error);
      setError(`Failed to load bootstrap nodes: ${error.message}`);
      setNodes([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchBootstrapNodes();
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(() => {
      fetchBootstrapNodes();
    }, 10000);
    
    return () => clearInterval(interval);
  }, [fetchBootstrapNodes]);

  const getStatusColor = (status) => {
    return status === 'active' ? '#FFFFFF' : '#666666';
  };

  const handleCreateNode = async () => {
    setError('');
    setSuccess('');
    try {
      const privateKey = localStorage.getItem('privateKey');
      if (!privateKey) {
        setError('Please login first');
        return;
      }

      const address = localStorage.getItem('address');
      const amountWei = (parseFloat(newNode.amount) * 1e18).toString();

      const result = await callRpcMethod('avo_createBootstrapStake', [{
        address: address,
        amount_wei: amountWei
      }]);

      setSuccess(`Bootstrap node created successfully! Stake ID: ${result.stake_id}`);
      setCreateDialog(false);
      setNewNode({ amount: '100000' });
      await fetchBootstrapNodes();
    } catch (error) {
      console.error('Error creating bootstrap node:', error);
      setError(error.message || 'Failed to create bootstrap node');
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  return (
    <ThemeProvider theme={strictMonochromeTheme}>
      <Box sx={{ minHeight: '100vh', backgroundColor: '#000000', color: '#FFFFFF' }}>
        {/* Header */}
        <AppBar position="static" sx={{ backgroundColor: '#000000', borderBottom: '1px solid #FFFFFF' }}>
          <Toolbar>
            <IconButton onClick={() => navigate('/dashboard')} sx={{ color: '#FFFFFF', mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Hub sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600, letterSpacing: '1px' }}>
              BOOTSTRAP NODES
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setCreateDialog(true)}
              sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', mr: 2 }}
            >
              CREATE NODE
            </Button>
            <IconButton onClick={fetchBootstrapNodes} sx={{ color: '#FFFFFF' }}>
              <Refresh />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, backgroundColor: '#000000', border: '1px solid #FFFFFF', color: '#FFFFFF', borderRadius: 0 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2, backgroundColor: '#000000', border: '1px solid #FFFFFF', color: '#FFFFFF', borderRadius: 0 }}>
              {success}
            </Alert>
          )}

          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', backgroundColor: '#000000', border: '1px solid #FFFFFF', borderRadius: 0 }}>
                <Hub sx={{ fontSize: 40, color: '#FFFFFF', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                  {networkMetrics?.total_nodes || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF', mt: 1, letterSpacing: '1px' }}>
                  TOTAL NODES
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', backgroundColor: '#000000', border: '1px solid #FFFFFF', borderRadius: 0 }}>
                <CheckCircle sx={{ fontSize: 40, color: '#FFFFFF', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                  {networkMetrics?.active_nodes || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF', mt: 1, letterSpacing: '1px' }}>
                  ACTIVE NODES
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', backgroundColor: '#000000', border: '1px solid #FFFFFF', borderRadius: 0 }}>
                <Speed sx={{ fontSize: 40, color: '#FFFFFF', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                  {networkMetrics?.total_stake_avo ? networkMetrics.total_stake_avo.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '0'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF', mt: 1, letterSpacing: '1px' }}>
                  TOTAL STAKED AVO
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', backgroundColor: '#000000', border: '1px solid #FFFFFF', borderRadius: 0 }}>
                <TrendingUp sx={{ fontSize: 40, color: '#FFFFFF', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                  {networkMetrics?.total_rewards_avo ? networkMetrics.total_rewards_avo.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '0.00'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF', mt: 1, letterSpacing: '1px' }}>
                  TOTAL REWARDS AVO
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Nodes List */}
          <Card sx={{ backgroundColor: '#000000', border: '1px solid #FFFFFF', borderRadius: 0 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #FFFFFF' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '2px', color: '#FFFFFF' }}>
                BOOTSTRAP NODES
              </Typography>
            </Box>
            
            {loading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress sx={{ color: '#FFFFFF' }} />
                <Typography sx={{ mt: 2, color: '#FFFFFF' }}>Loading bootstrap nodes...</Typography>
              </Box>
            ) : nodes.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Hub sx={{ fontSize: 60, color: '#FFFFFF', mb: 2 }} />
                <Typography sx={{ color: '#FFFFFF', mb: 1 }}>No bootstrap nodes yet</Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>Be the first to create a bootstrap node</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ borderBottom: '1px solid #FFFFFF' }}>
                      <TableCell sx={{ color: '#FFFFFF', fontWeight: 600, borderBottom: '1px solid #FFFFFF' }}>NODE</TableCell>
                      <TableCell sx={{ color: '#FFFFFF', fontWeight: 600, borderBottom: '1px solid #FFFFFF' }}>STAKE</TableCell>
                      <TableCell sx={{ color: '#FFFFFF', fontWeight: 600, borderBottom: '1px solid #FFFFFF' }}>REWARDS</TableCell>
                      <TableCell sx={{ color: '#FFFFFF', fontWeight: 600, borderBottom: '1px solid #FFFFFF' }}>APR</TableCell>
                      <TableCell sx={{ color: '#FFFFFF', fontWeight: 600, borderBottom: '1px solid #FFFFFF' }}>STATUS</TableCell>
                      <TableCell sx={{ color: '#FFFFFF', fontWeight: 600, borderBottom: '1px solid #FFFFFF' }}>CREATED</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nodes.map((node) => (
                      <TableRow key={node.id} sx={{ '&:hover': { backgroundColor: '#1A1A1A' }, borderBottom: '1px solid #FFFFFF' }}>
                        <TableCell sx={{ borderBottom: '1px solid #FFFFFF' }}>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5, color: '#FFFFFF' }}>
                              {node.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                              {node.address.slice(0, 10)}...{node.address.slice(-8)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#FFFFFF' }}>
                              ID: {node.id}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #FFFFFF' }}>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                              {node.stakeAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} AVO
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#FFFFFF' }}>
                              Staked
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #FFFFFF' }}>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                              {node.totalRewards.toLocaleString(undefined, { maximumFractionDigits: 4 })} AVO
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#FFFFFF' }}>
                              Pending: {node.pendingRewards.toLocaleString(undefined, { maximumFractionDigits: 4 })} AVO
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #FFFFFF' }}>
                          <Chip 
                            label={`${(node.apr * 100).toFixed(1)}%`}
                            sx={{ 
                              backgroundColor: '#000000',
                              border: '1px solid #FFFFFF',
                              color: '#FFFFFF',
                              fontWeight: 600,
                              borderRadius: 0
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #FFFFFF' }}>
                          <Chip 
                            label={node.status.toUpperCase()}
                            sx={{ 
                              backgroundColor: '#000000',
                              border: '1px solid #FFFFFF',
                              color: '#FFFFFF',
                              fontWeight: 600,
                              borderRadius: 0
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #FFFFFF' }}>
                          <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                            {formatTimestamp(node.createdAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </Box>

        {/* Create Node Dialog */}
        <Dialog 
          open={createDialog} 
          onClose={() => setCreateDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { backgroundColor: '#000000', border: '1px solid #FFFFFF', borderRadius: 0 } }}
        >
          <DialogTitle sx={{ borderBottom: '1px solid #FFFFFF', color: '#FFFFFF', fontWeight: 600, letterSpacing: '1px' }}>
            CREATE BOOTSTRAP NODE
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Stake Amount (AVO)"
                  type="number"
                  value={newNode.amount}
                  onChange={(e) => setNewNode({...newNode, amount: e.target.value})}
                  variant="outlined"
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: '#FFFFFF' } },
                    '& .MuiInputLabel-root': { color: '#FFFFFF' },
                    '& .MuiInputBase-input': { color: '#FFFFFF' }
                  }}
                  helperText="Minimum: 100,000 AVO for bootstrap node"
                  FormHelperTextProps={{ sx: { color: '#FFFFFF' } }}
                />
              </Grid>
            </Grid>
            <Alert 
              severity="info" 
              sx={{ mt: 2, backgroundColor: '#000000', border: '1px solid #FFFFFF', color: '#FFFFFF', borderRadius: 0 }}
            >
              Creating a bootstrap node will stake your AVO tokens. You'll earn rewards based on the network APR.
              Your staked tokens will help secure the network.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ borderTop: '1px solid #FFFFFF', p: 2 }}>
            <Button onClick={() => setCreateDialog(false)} sx={{ color: '#FFFFFF', borderRadius: 0 }}>CANCEL</Button>
            <Button 
              onClick={handleCreateNode}
              variant="outlined"
              sx={{ 
                borderColor: '#FFFFFF', 
                color: '#FFFFFF', 
                borderRadius: 0,
                '&:hover': { backgroundColor: '#FFFFFF', color: '#000000' }
              }}
            >
              CREATE NODE
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default BootstrapNodes;